import { repositoryClient } from "@/core/network/repository-client";
import type {
  SearchParams,
  SearchResponse,
  SearchResultsResponse,
  SearchSuggestResponse,
} from "../../types";
import type {
  SearchResponseDto,
  SearchResultsResponseDto,
  SearchSuggestResponseDto,
} from "../dto/search.dto";
import {
  mapSearchResponseDto,
  mapSearchResultsDto,
  mapSearchSuggestDto,
} from "../mappers/search.mapper";

const ENDPOINT = "/search";

/**
 * Build the query params object for the search endpoint, dropping any
 * undefined/empty values so we only send what the caller actually set.
 */
function toQueryParams(params: SearchParams): Record<string, string | boolean> {
  const query: Record<string, string | boolean> = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    query[key] = typeof value === "boolean" ? value : String(value);
  }

  return query;
}

function getSearchDto<TResponseDto extends SearchResponseDto>(
  params: SearchParams,
  signal?: AbortSignal,
): Promise<TResponseDto> {
  return repositoryClient.get<TResponseDto>(ENDPOINT, {
    queryParams: toQueryParams(params),
    // Public endpoint — no auth header required.
    useAuth: false,
    signal,
  });
}

export const searchRepository = {
  /** Calls the public `GET /search` endpoint and returns the feature response. */
  async search(params: SearchParams, signal?: AbortSignal): Promise<SearchResponse> {
    const dto = await getSearchDto<SearchResponseDto>(params, signal);

    return mapSearchResponseDto(dto);
  },

  /** Full results search. Defaults to searching everything. */
  async getResults(
    params: Omit<SearchParams, "mode">,
    signal?: AbortSignal,
  ): Promise<SearchResultsResponse> {
    const dto = await getSearchDto<SearchResultsResponseDto>(
      { ...params, mode: "results" },
      signal,
    );

    return mapSearchResultsDto(dto);
  },

  /** Lightweight autocomplete suggestions for a query. */
  async getSuggestions(q: string, limit = 8, signal?: AbortSignal): Promise<SearchSuggestResponse> {
    const dto = await getSearchDto<SearchSuggestResponseDto>({ q, mode: "suggest", limit }, signal);

    return mapSearchSuggestDto(dto);
  },
};
